/**
 * Crossword — motion vocabulary.
 *
 * The feel is "pencil and newsprint," distinct from Five's wooden-tile flip.
 * Nothing rotates in 3D; letters *set* onto the page (a small, fast ink-press)
 * and the active word lights along its length like a highlighter drawn across
 * paper. The springs are tuned softer and lighter than Five's — paper has less
 * mass than a wooden tile.
 *
 * These constants are bespoke (POLISH-GATE item 14): Five settles a tile at
 * { stiffness: 280, damping: 14 }; crossword sets a letter at a stiffer, more
 * critically-damped spring so it lands with a tiny tick and no jiggle, while
 * the page-level composition rises on a gentler, heavier spring.
 */

/** Page entry — the whole sheet eases up into place, unhurried. */
export const pageSpring = {
  type: 'spring' as const,
  stiffness: 170,
  damping: 26,
  mass: 1.2,
};

/** Hub card settle — a printed card pressed onto the desk. */
export const cardSpring = {
  type: 'spring' as const,
  stiffness: 220,
  damping: 24,
  mass: 0.95,
};

/** Hover lift on a hub card. */
export const hoverSpring = {
  type: 'spring' as const,
  stiffness: 340,
  damping: 24,
};

/** A letter setting onto the page — fast, crisp, barely a bounce. */
export const letterSetSpring = {
  type: 'spring' as const,
  stiffness: 720,
  damping: 30,
  mass: 0.5,
};

/** The active cell's light pool sliding to a new cell. */
export const cursorSpring = {
  type: 'spring' as const,
  stiffness: 480,
  damping: 34,
  mass: 0.7,
};

/** A check verdict blooming in — gentle, a held breath. */
export const markSpring = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 20,
  mass: 0.8,
};

/** The completion seal pressing down at the end. */
export const sealSpring = {
  type: 'spring' as const,
  stiffness: 240,
  damping: 16,
  mass: 1.0,
};

/** A soft nudge when an action has nowhere to go (no shake-shake alarm). */
export const nudgeSpring = {
  type: 'spring' as const,
  stiffness: 520,
  damping: 18,
};

// ---- Timings (ms) ---------------------------------------------------------

/** Press feedback when a key lands. */
export const PRESS_MS = 90;
/** Stagger between cells when a word's verdict cascades. */
export const MARK_STAGGER_MS = 70;
/** How long one cell's verdict takes to settle. */
export const MARK_SETTLE_MS = 420;
/** Stagger between letters when the celebration sweeps the solved grid. */
export const SOLVE_SWEEP_STAGGER_MS = 26;
/** Delay before the completion seal + card appear after the solve, so the
 * filled grid registers for a beat first. */
export const FINISH_DELAY_MS = 760;
/** Hub card entry stagger, in seconds (motion uses seconds for delays). */
export const HUB_STAGGER_S = 0.09;
